import React from "react";
import { useQuery } from "@tanstack/react-query";
// Assuming these are correctly located relative to ChatHeader.tsx
import { fetchRoomMeta } from "../services/api";
import type { RoomMeta } from "../../types";

// Simple Pencil SVG Icon Component
const EditIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={`h-4 w-4 ${className || ""}`} // Default size, allow override
    aria-hidden="true" // Hide from screen readers as it's decorative or has text alternative
  >
    <path d="M5.433 13.917l1.262-3.155A4 4 0 017.58 9.42l6.92-6.918a2.121 2.121 0 013 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 01-.65-.65z" />
    <path d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0010 3H4.75A2.75 2.75 0 002 5.75v9.5A2.75 2.75 0 004.75 18h9.5A2.75 2.75 0 0017 15.25V10a.75.75 0 00-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5z" />
  </svg>
);

interface ChatHeaderProps {
  roomId: string;
  connectionStatus: string;
  username: string;
  onEditUsername: () => void;
  isSidebar?: boolean;
  isYoutubeRoom?: boolean;
}

export function ChatHeader({
  roomId,
  connectionStatus,
  username,
  onEditUsername,
  isSidebar = false,
  isYoutubeRoom = false,
}: ChatHeaderProps) {
  // Fetch Room Metadata using TanStack Query
  const {
    data: roomMeta,
    isLoading: isLoadingRoomMeta,
    error: roomMetaError,
  } = useQuery<RoomMeta, Error>({
    queryKey: ["roomMeta", roomId], // Query key includes the dynamic roomId
    queryFn: () => fetchRoomMeta(roomId), // Function to fetch data
    enabled: !!roomId, // Only run the query if roomId is truthy
    staleTime: 5 * 60 * 1000, // Data is considered fresh for 5 minutes
    retry: 1, // Retry failed requests once
  });

  // Handler for image loading errors
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    // Hide the image element if it fails to load
    (e.target as HTMLImageElement).style.display = "none";
  };

  return (
    <div
      className={`mb-3 flex flex-col gap-2 border-b border-slate-200 pb-3 ${
        isSidebar ? "pt-1" : "" // Adjust top padding in sidebar mode
      }`}
    >
      <div className="flex min-h-[40px] items-start gap-3">
        {/* Image/Placeholder Section */}
        <div className="flex-shrink-0 pt-1">
          {/* Loading State */}
          {isLoadingRoomMeta && (
            <div className="h-8 w-8 animate-pulse rounded bg-slate-200"></div>
          )}
          {/* Image Display */}
          {roomMeta?.image && !isLoadingRoomMeta && !roomMetaError && (
            <img
              src={roomMeta.image}
              alt="Room image"
              className="h-8 w-8 rounded object-cover"
              onError={handleImageError} // Handle loading errors
            />
          )}
          {/* Placeholder if no image and not loading/error */}
          {!isLoadingRoomMeta && !roomMeta?.image && !roomMetaError && (
            <div className="h-8 w-8 rounded bg-slate-100"></div>
          )}
          {/* Placeholder on image load error */}
          {roomMetaError && ( // Show placeholder if there was an error fetching metadata
            <div className="h-8 w-8 rounded border border-red-300 bg-red-100"></div>
          )}
        </div>

        {/* Title/Room ID Section */}
        <div className="flex-grow overflow-hidden">
          {/* Loading State */}
          {isLoadingRoomMeta && (
            <div className="mb-1 h-5 w-3/4 animate-pulse rounded bg-slate-200"></div>
          )}
          {/* Error State */}
          {roomMetaError && !isLoadingRoomMeta && (
            <p
              className="text-sm text-red-600"
              title={roomMetaError.message} // Show full error on hover
            >
              Error loading title
            </p>
          )}
          {/* Title Display */}
          {roomMeta && !isLoadingRoomMeta && !roomMetaError && (
            <h1
              className={`truncate font-semibold text-slate-800 ${
                isSidebar ? "text-md" : "text-lg sm:text-xl" // Adjust size for sidebar
              }`}
              title={roomMeta.title || roomId} // Show full title or room ID on hover
            >
              {roomMeta.title || "Chat Room"} {/* Fallback title */}
            </h1>
          )}
          {/* Room ID Display (conditional) */}
          {(!isYoutubeRoom || !roomMeta?.title || roomMetaError) &&
            !isLoadingRoomMeta && ( // Show Room ID if not YT, or no title, or error loading title
              <p
                className={`truncate text-xs text-slate-500 ${
                  isSidebar ? "mt-0.5" : ""
                }`}
                title={roomId} // Show full room ID on hover
              >
                {roomId}
              </p>
            )}
        </div>

        {/* User Info & Connection Status Section */}
        <div className="ml-auto flex flex-shrink-0 flex-col items-end text-right">
          {/* Container for username and edit icon */}
          <div
            onClick={onEditUsername} // Make the whole area clickable
            title="Click to change username"
            className="group flex cursor-pointer items-center gap-1 rounded px-1 py-0.5 hover:bg-slate-100" // Added padding, group for hover effect
          >
            <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">
              {username} {/* Display username */}
            </span>
            <EditIcon className="text-slate-400 group-hover:text-indigo-600" />{" "}
            {/* Add icon */}
          </div>
          {/* Connection Status Display */}
          <span className="mt-0.5 text-xs text-slate-500">
            {connectionStatus} {/* Display connection status below */}
          </span>
        </div>
      </div>
    </div>
  );
}
